import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useLayoutEffect } from 'react';

import * as actions from '../../../../redux/actions';
import {
    getConfig,
    getAuthUser,
    getIsFetching,
    getErrorMessage,
    getSuccessMessage,
} from '../../../../redux/selectors';

import { WrapAvatar } from './AccountDetails.stc';
import FileUpload from '../../../FileUpload';
import Button from '../../../Button';
import Image from '../../../Image';
import Input from '../../../Input';
import Label from '../../../Label';
import Alert from '../../../Alert';
import Field from '../../../Field';
import Card from '../../../Card';

const Avatar = styled(Image)`
    width: 225px;
    height: 225px;
    object-fit: cover;
`;

const HelpText = styled.p`
    margin-top: 30px;
    padding-left: 25px;
    border-left: 1px solid var(--color-border-500);
`;

function AccountDetails(props) {
    const dispatch = useDispatch();
    const config = useSelector(getConfig);
    const user = useSelector(getAuthUser) || {};
    const errorMessage = useSelector(getErrorMessage);
    const successMessage = useSelector(getSuccessMessage);

    const handleUploadSuccess = (result) => {
        dispatch(actions.updateUser(user.id, { avatar: result.url }));
    };
    const handleUploadError = (error) => {
        console.log('ANWARN', error);
    };

    const handleRemoveAvatar = (e) => {
        dispatch(actions.updateUser(user.id, { avatar: null }));
    };

    useLayoutEffect(() => {
        window.scrollTo({ top: 0 });
    }, [errorMessage, successMessage]);

    useEffect(() => {
        return () => dispatch(actions.clearUserMessage());
    }, [dispatch]);

    return (
        <div className="container">
            <Helmet>
                <title>Account Details</title>
            </Helmet>
            <div className="flex">
                <div className="col-md-1/1">
                    <Card
                        size="lg"
                        header={<h3>Account Information</h3>}
                        footer={
                            <div className="card-footer">
                                <span>
                                    Need Help?{' '}
                                    <Button type="link">
                                        Account Documentation
                                    </Button>
                                </span>
                            </div>
                        }
                    >
                        {successMessage && (
                            <div style={{ marginBottom: 20 }}>
                                <Alert type="success">{successMessage}</Alert>
                            </div>
                        )}
                        {errorMessage && (
                            <div style={{ marginBottom: 20 }}>
                                <Alert type="error">{errorMessage}</Alert>
                            </div>
                        )}

                        <div className="flex">
                            <div className="col-md-3/5">
                                <UpdateInfo user={user} />
                            </div>
                            <div className="col-md-2/5">
                                <WrapAvatar>
                                    <Avatar
                                        type="circle"
                                        src={
                                            user.avatar ||
                                            '/assets/images/user-thumb.png'
                                        }
                                    />
                                    <div className="spread-items spc-a mt-25">
                                        <FileUpload
                                            onError={handleUploadError}
                                            onSuccess={handleUploadSuccess}
                                            url={`${config.apiURL}/upload`}
                                        >
                                            <Button type="primary-alt">
                                                Upload
                                            </Button>
                                        </FileUpload>
                                        {user.avatar && (
                                            <Button
                                                type="danger-alt"
                                                onClick={handleRemoveAvatar}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </div>
                                </WrapAvatar>
                            </div>
                        </div>
                        <h3>Change Password</h3>
                        <hr style={{ marginBottom: 25 }} />
                        <ChangePassword user={user} />
                    </Card>
                </div>
            </div>
        </div>
    );
}

export default AccountDetails;

function UpdateInfo({ user }) {
    const dispatch = useDispatch();
    const [state, setState] = useState({});
    const isFetching = useSelector(getIsFetching);

    const handleInput = (e) => {
        const { name, value } = e.target;
        setState((state) => ({ ...state, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const input = JSON.parse(JSON.stringify(state));
        if (input.name || input.email) {
            dispatch(
                actions.updateUser(user.id, {
                    ...input,
                    email: input.email?.toLowerCase(),
                })
            );
        }
    };

    return (
        <form onSubmit={handleSubmit} className="column-inner">
            <Field>
                <Label>Name</Label>
                <Input
                    name="name"
                    onChange={handleInput}
                    defaultValue={user.name}
                />
            </Field>
            <Field>
                <Label>Email Address</Label>
                <Input
                    name="email"
                    onChange={handleInput}
                    defaultValue={user.email}
                />
            </Field>
            <Field>
                <Button htmlType="submit" size="2x" disabled={isFetching}>
                    Save Changes
                </Button>
            </Field>
        </form>
    );
}

function ChangePassword({ user }) {
    const dispatch = useDispatch();
    const [state, setState] = useState({});
    const isFetching = useSelector(getIsFetching);

    const isButtonDisabled = state.newPassword
        ? state.newPassword !== state.confirmPassword
        : false;

    const handleInput = (e) => {
        const { name, value } = e.target;
        setState((state) => ({ ...state, [name]: value }));
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        const input = JSON.parse(JSON.stringify(state));

        if (input.newPassword === input.confirmPassword) {
            delete input.confirmPassword;
            dispatch(actions.updateUser(user.id, input));
        }
    };

    return (
        <div className="flex">
            <div className="col-md-3/5">
                <form className="column-inner" onSubmit={handleSubmit}>
                    <Field>
                        <Label>Current Password</Label>
                        <Input
                            type="password"
                            name="currentPassword"
                            onChange={handleInput}
                        />
                    </Field>
                    <Field>
                        <Label>New Password</Label>
                        <Input
                            type="password"
                            name="newPassword"
                            onChange={handleInput}
                        />
                    </Field>
                    <Field>
                        <Label>Confirm New Password</Label>
                        <Input
                            type="password"
                            onChange={handleInput}
                            name="confirmPassword"
                        />
                    </Field>
                    <Field>
                        <Button
                            size="2x"
                            htmlType="submit"
                            disabled={isButtonDisabled || isFetching}
                        >
                            Change Password
                        </Button>
                    </Field>
                </form>
            </div>
            <div className="col-md-2/5">
                <HelpText>
                    Select a strong password longer than 8 character and mixed
                    with numbers and special characters.
                </HelpText>
            </div>
        </div>
    );
}
